import { motion, Variants } from 'framer-motion';
import React from 'react';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';

interface MetricCardProps {
  value: string;
  label: string;
  icon: React.ReactElement;
  index: number;
}

export const MetricCard: React.FC<MetricCardProps> = ({ value, label, icon, index }) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  const getParts = (val: string) => {
    const match = val.match(/([^\d.]*)(\d+(?:,\d{3})*(?:\.\d+)?)(.*)/);
    if (match) {
      const [, pre, numStr, post] = match;
      const numericValue = parseFloat(numStr.replace(/,/g, ''));
      return {
        prefixText: pre || '',
        numericValue: numericValue,
        suffixText: post || '',
      };
    }
    const numericMatch = val.match(/[\d.]+/);
    if (numericMatch) {
      const numStr = numericMatch[0];
      const numericVal = parseFloat(numStr.replace(/,/g, ''));
      const parts = val.split(numStr);
      return {
        prefixText: parts[0] || '',
        numericValue: numericVal,
        suffixText: parts[1] || '',
      };
    }
    return { prefixText: '', numericValue: 0, suffixText: val };
  };

  const { prefixText, numericValue, suffixText } = getParts(value);

  const cardVariants: Variants = {
    hidden: { opacity: 0, y: 50, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        delay: index * 0.15,
        duration: 0.6,
        ease: [0.6, -0.05, 0.01, 0.99],
      },
    },
  };

  return (
    <motion.div
      ref={ref}
      variants={cardVariants}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      className="group relative overflow-hidden rounded-2xl bg-white bg-opacity-80 p-6 text-center shadow-lg backdrop-blur-md transition-all duration-300 hover:bg-opacity-20 hover:shadow-xl hover:-translate-y-2"
    >
      {/* Glow effect on hover */}
      <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-brand-primary/30 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-50 group-hover:animate-pulse" />

      <div className="relative z-10 flex flex-col items-center">
        <div className="mb-4 text-brand-primary transition-transform duration-300 group-hover:scale-110">
          {React.cloneElement(icon as any, { size: 36 })}
        </div>
        <div className="text-4xl md:text-5xl font-bold text-government-navy drop-shadow-lg">
          {inView && (
            <CountUp
              start={0}
              end={numericValue}
              duration={2.5}
              separator=","
              decimals={numericValue % 1 !== 0 ? 1 : 0}
              prefix={prefixText}
              suffix={suffixText}
            />
          )}
        </div>
        <p className="mt-2 text-lg font-medium text-government-navy text-opacity-80">{label}</p>
      </div>
    </motion.div>
  );
};